import * as React from 'react';
import { parse } from 'date-fns';
import { getLocale } from '../../../../../utils';
import { getCurrency } from '../../../../../cache';
import { setEveryTravelCartIdAction } from '../../../../store/everyTravel/actions';
import { getTravellersValues } from '../utils';
import { WidgetCart, WidgetInitProps } from '@websky/every-travel/src/types';
import { CheckoutOrderFragment, OrderStatus, SegmentFragment, WidgetAdditionalServiceFragment } from '@websky/graphql';
import { useTheme } from '../../../../../theme';
import { useDispatch } from 'react-redux';
import EveryTravelInput from '../EveryTravelInput/EveryTravelInput';
import { EngineTheme as EveryTravelEngine } from '@websky/every-travel/src/theme';
import { SearchFormTheme } from '@websky/every-travel/src/Widget/CostaBrava/CostaBrava';

const EveryTravelWidget = React.lazy(() => import('@websky/every-travel/src/Widget/Widget'));

interface Props {
	everyTravel: WidgetAdditionalServiceFragment;
	segment: SegmentFragment;
	order: CheckoutOrderFragment;
	setEveryTravelCart: (cart: WidgetCart) => void;
	everyTravelStyles?: EveryTravelEngine;
	everyTravelSearchFormStyles?: SearchFormTheme;
	needToConfirmCart?: boolean;
}

const EveryTravelWrapper: React.FC<Props> = ({
	everyTravel,
	segment,
	order,
	setEveryTravelCart,
	everyTravelStyles,
	everyTravelSearchFormStyles,
	needToConfirmCart
}) => {
	const locale = getLocale(),
		currency = getCurrency();

	const theme = useTheme('Checkout').ServicesAggregationPage;
	const dispatch = useDispatch();

	const updateCart = React.useCallback(
		(cart: WidgetCart) => {
			setEveryTravelCart(cart);

			dispatch(
				setEveryTravelCartIdAction({
					cartId: cart?.cartId
				})
			);
		},
		[setEveryTravelCart, dispatch]
	);

	const everyTravelParams: WidgetInitProps = React.useMemo(
		() =>
			(({
				cartId: everyTravel.initializeParameters.cartId,
				orderId: everyTravel.initializeParameters.orderId,
				userId: everyTravel.initializeParameters.userId,
				arrDate: everyTravel.initializeParameters.arrDate
					? parse(everyTravel.initializeParameters.arrDate, 'yyyy-MM-dd', new Date())
					: null,
				depDate: everyTravel.initializeParameters.depDate
					? parse(everyTravel.initializeParameters.depDate, 'yyyy-MM-dd', new Date())
					: null,
				apiEndpoint: everyTravel.endpoint,
				authToken: everyTravel.authToken,
				currency,
				widgetLocale: locale,
				containerClassName: theme.container,
				city: {
					name: segment.arrival.airport.city?.name,
					countryIso: everyTravel.initializeParameters.countryIso2,
					countryId: everyTravel.initializeParameters.countryGeonamesId,
					imageUrl: segment.arrival.airport.city?.images?.panorama?.url,
					latitude: parseFloat(segment.arrival.airport.latitude),
					longitude: parseFloat(segment.arrival.airport.longitude)
				},
				travellers: getTravellersValues(everyTravel.initializeParameters.travellers, order.customer.values),
				initCart: setEveryTravelCart,
				updateCart,
				renderInput: EveryTravelInput,
				styles: everyTravelStyles,
				searchFormStyles: everyTravelSearchFormStyles,
				canCreateNewCart: order.status !== OrderStatus.Cancelled,
				canModifyOrderedCart:
					order.status === OrderStatus.New ||
					order.status === OrderStatus.Booked ||
					(order.status === OrderStatus.Confirmed && everyTravel.state === 'new')
			} as unknown) as WidgetInitProps),
		[everyTravel, currency, locale, segment, order, updateCart, everyTravelStyles, everyTravelSearchFormStyles]
	);

	return (
		<React.Suspense fallback={<div>Loading...</div>}>
			<EveryTravelWidget config={everyTravelParams} needToConfirmCart={needToConfirmCart} />
		</React.Suspense>
	);
};

export default EveryTravelWrapper;
